import React, { useEffect, useState } from "react";
import { api, Banner, Button, Field } from "./ui.tsx";
import type { VisitsConfig } from "../shared/visits.ts";

type Report = {
  source: { id: string; name: string };
  state: string;
  synchronizedAt: string | null;
  message: string;
  tabs: Array<{
    id: string;
    title: string;
    available: boolean;
    sourceRecords: number | null;
    uniqueAddresses: number | null;
    visited: number | null;
    pending: number | null;
    analysis: number | null;
    missingGroup: number | null;
    groups: Array<{ id: string; name: string }>;
  }>;
  unmappedTabs: Array<{ sheetId: number; title: string; reason: string }>;
};
const number = (value: number | null) =>
  value === null
    ? "Indisponível"
    : new Intl.NumberFormat("pt-BR").format(value);
export function VisitsReadiness({ config }: { config: VisitsConfig }) {
  const [sourceId, setSourceId] = useState(
    config.sources.find((s) => s.enabled)?.id ?? "",
  );
  const [report, setReport] = useState<Report | null>(null),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false),
    [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!sourceId) return;
    const controller = new AbortController();
    setLoading(true);
    setReport(null);
    setError("");
    api<Report>(
      "/visits-overview/delegation-readiness?sourceId=" +
        encodeURIComponent(sourceId),
      "GET",
      undefined,
      controller.signal,
    )
      .then((value) => {
        if (!controller.signal.aborted) setReport(value);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setError("Não foi possível consultar as abas. Tente novamente.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [sourceId, retry, config.version]);
  return (
    <section className="vo-surface" aria-busy={loading}>
      <h2>Preparar delegações</h2>
      <p>
        Confira cada aba da planilha antes de atribuir seu trabalho a um
        assessor. Esta consulta usa a configuração publicada.
      </p>
      <Field label="Planilha de origem">
        <select
          value={sourceId}
          onChange={(event) => setSourceId(event.target.value)}
        >
          {config.sources
            .filter((s) => s.enabled)
            .map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
        </select>
      </Field>
      {loading && <p role="status">Consultando as abas…</p>}
      {error && (
        <Banner kind="danger">
          {error}{" "}
          <Button onClick={() => setRetry((n) => n + 1)}>
            Tentar novamente
          </Button>
        </Banner>
      )}
      {!sourceId && <p>Nenhuma fonte ativa disponível.</p>}
      {report && (
        <>
          <Banner kind="warn">{report.message}</Banner>
          <p className="small muted">
            Um endereço pode aparecer em mais de uma aba. Não some os subtotais
            para obter o total geral. A situação considera todas as ocorrências
            autorizadas, inclusive fora da aba.
          </p>
          <ul className="vo-list">
            {report.tabs.map((tab) => (
              <li key={tab.id} className="vo-record">
                <h3>{tab.title}</h3>
                <p>
                  <strong>{number(tab.uniqueAddresses)}</strong> endereços
                  únicos · {number(tab.sourceRecords)} registros de origem
                </p>
                {tab.available ? (
                  <p>
                    {number(tab.visited)} visitados · {number(tab.pending)}{" "}
                    pendentes · {number(tab.analysis)} em análise
                  </p>
                ) : (
                  <p>Primeira leitura ainda indisponível.</p>
                )}
                {!!tab.missingGroup && (
                  <p>
                    {number(tab.missingGroup)} endereços sem grupo confirmado.
                  </p>
                )}
                <details>
                  <summary>Grupos registrados nesta aba</summary>
                  <p>
                    {tab.groups.map((g) => g.name).join(" · ") ||
                      "Nenhum grupo confirmado."}
                  </p>
                  <p>
                    O título da aba não comprova a localização dos endereços.
                  </p>
                </details>
              </li>
            ))}
          </ul>
          {!!report.unmappedTabs.length && (
            <details>
              <summary>
                Abas ainda fora da consolidação ({report.unmappedTabs.length})
              </summary>
              <ul>
                {report.unmappedTabs.map((tab) => (
                  <li key={tab.sheetId}>
                    <strong>{tab.title}</strong>: {tab.reason}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      )}
    </section>
  );
}
